import { useQuery } from "@tanstack/react-query";

/** Shape of GET /api/documents/check-limit. */
export interface DocumentLimit {
  canUpload: boolean;
  currentCount: number;
  limit: number;
  plan: string;
  messagePt?: string | null;
  messageEn?: string | null;
}

export const DOCUMENT_LIMIT_KEY = ["/api/documents/check-limit"] as const;

/**
 * Monthly document quota of the signed-in user, shared by every "new document" entry point.
 * `reached` is false while loading or when the check fails (the server still refuses the upload).
 */
export function useDocumentLimit() {
  const query = useQuery<DocumentLimit>({ queryKey: [...DOCUMENT_LIMIT_KEY], staleTime: 30_000, refetchOnWindowFocus: true });
  const data = query.data;
  const reached = !!data && data.limit !== -1 && !data.canUpload;
  const lastOne = !!data && data.limit !== -1 && data.canUpload && data.limit - data.currentCount === 1;
  return { data, reached, lastOne, loading: query.isLoading };
}

/** First day of next month in the user's locale (when the quota resets). */
export function quotaResetLabel(lang: string) {
  const d = new Date();
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return next.toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", { day: "numeric", month: "long" });
}

/** Short reason for a disabled "new document" button. */
export function limitReason(limit: DocumentLimit, lang: string) {
  if (lang === "pt") return `Você usou os ${limit.limit} documentos do mês no plano ${planName(limit.plan)}. Libera de novo em ${quotaResetLabel("pt")}, ou faça upgrade.`;
  return `You used this month's ${limit.limit} documents on the ${planName(limit.plan)} plan. It resets on ${quotaResetLabel("en")}, or upgrade.`;
}

export function planName(plan: string) {
  switch ((plan || "").toLowerCase()) {
    case "free": return "Free";
    case "pro": return "Professional";
    case "enterprise": return "Enterprise";
    case "customenterprise": case "custom_enterprise": case "custom": return "Custom";
    default: return plan;
  }
}
